import { lazy, Suspense, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { SkipToMain } from '@/shell';
import CommandPalette from '@/components/common/CommandPalette';
import AIAssistantFab from '@/components/AIAssistantFab';
import SetupProgressWidget from '@/components/SetupProgressWidget';
import HelpLauncher from '@/components/common/HelpLauncher';
import SystemAlertBar from '@/components/SystemAlertBar';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import PaymasterContextBanner from '@/components/PaymasterContextBanner';
import {
  OperatedFocusProvider,
  useOperatedFocus,
} from '@/contexts/OperatedFocusContext';
import { TYPE } from '@/theme/typography';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { DEFAULT_PALETTE, getPaletteByKey, getSidebarColors } from '@/config/colorPalettes';
import { PLATFORM_OWNER } from '@/config/urls';
import { NEUTRAL_COLORS } from '@/theme/colorTokens';
import { useEtBarModel, useInterplayAccent, useInterplayShellLoader } from '@/interplay/hooks';

// The ET bar (interplay HF1) is its own chunk: with VITE_INTERPLAY_SHELL_ENABLED off it is never
// fetched, and neither is its stylesheet.
const EtBar = lazy(() => import('@/interplay/EtBar'));
const ET_BAR_PX = 56; // 3px stripe + 52px row + 1px border
const ET_BAR_CTX_PX = 40;

const RAW_SHA: string = import.meta.env.VITE_GIT_SHA ?? 'local';
const RAW_TIME: string = import.meta.env.VITE_BUILD_TIME ?? '';
const APP_ENV: string = import.meta.env.VITE_ENVIRONMENT ?? 'local';

function formatBuildTime(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

function BuildStamp() {
  const isLocal = RAW_SHA === 'local' || APP_ENV === 'local';
  const sha = RAW_SHA !== 'local' ? RAW_SHA.slice(0, 7) : null;
  const date = formatBuildTime(RAW_TIME);

  if (isLocal) {
    return <span style={{ opacity: 0.6 }}>LOCAL DEV</span>;
  }

  return (
    <>
      {sha && <span>#{sha}</span>}
      {date && <span style={{ opacity: 0.7 }}>&middot; {date}</span>}
    </>
  );
}

export default function AppShell() {
  return (
    <OperatedFocusProvider>
      <AppShellInner />
    </OperatedFocusProvider>
  );
}

function AppShellInner() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const mobileOpen = useUIStore((s) => s.mobileSidebarOpen);
  const setMobileOpen = useUIStore((s) => s.setMobileSidebarOpen);
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);

  const darkMode = useUIStore((s) => s.darkMode);
  const sidebarStyle = useUIStore((s) => s.sidebarStyle);
  const paletteKey = useUIStore((s) => s.groupPaletteKey);

  const companies = useAuthStore((s) => s.companies);
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const activeCompany = companies.find((c) => c.company_id === activeCompanyId);

  // Sprint 4.5: paymaster theme shift. When a child page declares
  // operated focus, override the active company's colour so the
  // navbar accent + sidebar bar reflect the operated co's brand.
  // Reverts to the active company's colour the moment focus clears.
  const { focus: operatedFocus } = useOperatedFocus();
  // Interplay HF1: a connected customer's accent (contrast-checked; undefined when off, not
  // connected, or no brand) goes through the same brand path as the company colour.
  useInterplayShellLoader();
  const etBar = useEtBarModel();
  const interplayAccent = useInterplayAccent();
  const baseColour = interplayAccent || activeCompany?.company_colour || DEFAULT_PALETTE.primary;
  const etBarPx = etBar ? ET_BAR_PX + (etBar.hasContextRow ? ET_BAR_CTX_PX : 0) : 0;
  useEffect(() => {
    if (!etBarPx) return undefined;
    const root = document.documentElement;
    root.style.setProperty('--et-bar-total', `${etBarPx}px`);
    return () => {
      root.style.removeProperty('--et-bar-total');
    };
  }, [etBarPx]);
  const companyColour = operatedFocus?.companyColour || baseColour;

  // Sidebar color scheme — shared with footer
  const palette = getPaletteByKey(paletteKey);
  const sc = getSidebarColors(palette, sidebarStyle, darkMode);

  const layout = (
    <Box sx={{ display: 'flex', minHeight: etBar ? `calc(100vh - ${etBarPx}px)` : '100vh' }}>
      {!etBar && <SkipToMain />}
      {/* Sidebar — permanent on desktop, temporary on mobile */}
      <Sidebar
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        variant={isMobile ? 'temporary' : 'permanent'}
        collapsed={sidebarCollapsed}
        companyColour={companyColour}
        brandStripe={!etBar}
      />

      {/* Skip-to-content link for keyboard navigation */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          zIndex: -1,
        }}
        onFocus={(e) => {
          e.currentTarget.style.left = '16px';
          e.currentTarget.style.top = '16px';
          e.currentTarget.style.width = 'auto';
          e.currentTarget.style.height = 'auto';
          e.currentTarget.style.zIndex = '9999';
          e.currentTarget.style.padding = '8px 16px';
          e.currentTarget.style.backgroundColor = DEFAULT_PALETTE.primary;
          e.currentTarget.style.color = NEUTRAL_COLORS.white;
          e.currentTarget.style.borderRadius = '4px';
          e.currentTarget.style.textDecoration = 'none';
        }}
        onBlur={(e) => {
          e.currentTarget.style.left = '-9999px';
          e.currentTarget.style.width = '1px';
          e.currentTarget.style.height = '1px';
          e.currentTarget.style.zIndex = '-1';
        }}
      >
        Skip to content
      </a>

      {/* Main content area — flexGrow fills remaining space after sidebar */}
      <Box
        component="main"
        id="main-content"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0, // critical: allows flex children to shrink below content size
          height: 'calc(100vh - var(--et-bar-total, 0px))',
          overflow: 'hidden',
          transition: 'width 0.3s ease',
        }}
      >
        <Navbar
          companyColour={companyColour}
          brandStripe={!etBar}
        />

        {/* Platform-admin impersonation notice — renders only when active */}
        <ImpersonationBanner />

        {/* Cross-company payroll context — renders only for paymaster /
            operated companies; no-op for standalone. */}
        <PaymasterContextBanner />

        {/* Single prioritised alert strip: past_due > exceeded > trial-critical
            > critical-usage > trial-info > warning-usage. Only one shows. */}
        <SystemAlertBar onUpgrade={() => { window.location.href = '/settings#billing-plans'; }} />

        {/* Page content — scrollable area below sticky navbar */}
        <Box
          sx={{
            flex: 1,
            bgcolor: 'background.default',
            width: '100%',
            minWidth: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ width: '100%', minWidth: 0, maxWidth: '1400px', p: { xs: 2, sm: 3, md: 4 }, mx: 'auto', overflowX: 'hidden' }}>
            <Outlet />
          </Box>
        </Box>

        {/* Footer — matches sidebar color scheme when colored/dark */}
        <Box
          component="footer"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            py: 1.5,
            px: 3,
            fontSize: TYPE.base,
            bgcolor: sc.isInverted ? sc.bg : 'background.paper',
            color: sc.isInverted ? sc.textSecondary : 'text.secondary',
            borderTop: '1px solid',
            borderColor: sc.isInverted ? sc.divider : 'divider',
            position: 'relative',
            transition: 'background-color 0.3s ease, color 0.3s ease',
          }}
        >
          {/* Left divider */}
          <Box sx={{ flex: 1, height: '1px', bgcolor: sc.isInverted ? sc.divider : 'divider' }} />

          {/* Center content */}
          <Box sx={{ textAlign: 'center', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>
              &copy; {new Date().getFullYear()} {PLATFORM_OWNER}
              {' '}&middot;{' '}
              <BuildStamp />
            </span>
          </Box>

          {/* Right divider */}
          <Box sx={{ flex: 1, height: '1px', bgcolor: sc.isInverted ? sc.divider : 'divider' }} />
        </Box>
      </Box>

      {/* Command Palette — always mounted */}
      <CommandPalette />

      {/* AI Assistant — floating button + drawer, every page */}
      <AIAssistantFab />

      {/* Setup progress — Stripe-style floating checklist, only while wizard incomplete */}
      <SetupProgressWidget />

      {/* Unified help & support launcher — bottom-left. Consolidates the old
          chat bubble, footer help icon, and Help Center / User Guide links into
          one Stripe-style menu. Crisp takes over when configured. */}
      <HelpLauncher />

    </Box>
  );

  // Always the same tree shape, so the page under <Outlet /> never remounts when the bar arrives.
  return (
    <>
      {etBar ? <SkipToMain /> : null}
      {etBar ? (
        <Suspense fallback={<Box sx={{ height: etBarPx }} />}>
          <EtBar model={etBar} dark={darkMode} />
        </Suspense>
      ) : null}
      {layout}
    </>
  );
}
